import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

/**
 * Phase 15.5 P5 Batch F — Crew Seeder repair panel.
 *
 * Three searchable pickers (inviter / joined-member / crew) + reason input.
 * Reuses the same autocomplete UX as the Assign Referrer modal. Powers:
 *   - POST /api/admin/crew-seeds/assign
 *   - GET  /api/admin/crew-seeds/history/{user_id}
 *   - GET  /api/admin/referrals/search   (user autocomplete)
 *   - GET  /api/admin/crews/search       (crew autocomplete — new)
 */

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(d).slice(0, 10);
  }
};

const UserPicker = ({ label, selected, onChange, exclude = [], testIdPrefix }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected) return; // freeze when chosen
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/referrals/search", {
          params: { q, filter: "all", limit: 8 },
        });
        if (cancelled) return;
        setResults((r.data.rows || []).filter((u) => !exclude.includes(u.user_id)));
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, selected, exclude]);

  if (selected) {
    return (
      <div>
        <label className="text-xs text-gray-400 block mb-1">{label}</label>
        <div
          className="flex items-center gap-3 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/40"
          data-testid={`${testIdPrefix}-selected`}
        >
          <img
            src={getProfilePhotoUrl(selected)}
            alt={selected.artist_name}
            className="w-9 h-9 rounded-full object-cover"
          />
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{selected.artist_name}</div>
            <div className="text-[11px] text-gray-400 truncate">
              {selected.email || "—"} · <code className="text-purple-300">{selected.user_id}</code>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { onChange(null); setSearch(""); setResults([]); }}
            className="border-gray-700 text-gray-300"
            data-testid={`${testIdPrefix}-clear`}
          >
            Change
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative">
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by artist, email, code, or user_id…"
        className="bg-black/40 border-gray-700 text-white"
        data-testid={`${testIdPrefix}-search`}
      />
      {(loading || results.length > 0 || (search.trim().length >= 2 && !loading && results.length === 0)) && (
        <div
          className="absolute left-0 right-0 mt-1 bg-[#0e0e16] border border-purple-500/40 rounded-lg shadow-xl max-h-64 overflow-y-auto z-20"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {loading ? (
            <div className="p-3 text-xs text-gray-400">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-xs text-gray-400">No match.</div>
          ) : (
            results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => { onChange(u); setSearch(u.artist_name || ""); setResults([]); }}
                className="w-full text-left flex items-center gap-2 p-2 hover:bg-purple-500/10 border-b border-gray-800 last:border-b-0 transition-colors"
                data-testid={`${testIdPrefix}-result-${u.user_id}`}
              >
                <img
                  src={getProfilePhotoUrl(u)}
                  alt={u.artist_name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{u.artist_name}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {u.email || "—"} · <code className="text-purple-300">{u.user_id}</code>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CrewPicker = ({ label, selected, onChange, requireMemberId, testIdPrefix }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected) return;
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/crews/search", { params: { q, limit: 10 } });
        if (!cancelled) setResults(r.data.rows || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, selected]);

  if (selected) {
    const memberOK = !requireMemberId || (selected.member_ids || []).includes(requireMemberId);
    return (
      <div>
        <label className="text-xs text-gray-400 block mb-1">{label}</label>
        <div
          className={`flex items-center gap-3 p-2 rounded-md border ${memberOK ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-rose-500/10 border-rose-500/40'}`}
          data-testid={`${testIdPrefix}-selected`}
        >
          {selected.cover_image && (
            <img src={optimizeImage(selected.cover_image, 'avatar')} alt={selected.name} className="w-9 h-9 rounded-md object-cover" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium truncate">{selected.name}</div>
            <div className="text-[11px] text-gray-400 truncate">
              {selected.member_count} members · {selected.visibility || "—"} · <code className="text-purple-300">{selected.crew_id}</code>
            </div>
            {requireMemberId && !memberOK && (
              <div className="text-[10px] text-rose-300 mt-0.5">
                ⚠ Joined user is NOT a member of this crew — add them via the crew page first.
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { onChange(null); setSearch(""); setResults([]); }}
            className="border-gray-700 text-gray-300"
            data-testid={`${testIdPrefix}-clear`}
          >
            Change
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative">
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search crews by name or crew_id…"
        className="bg-black/40 border-gray-700 text-white"
        data-testid={`${testIdPrefix}-search`}
      />
      {(loading || results.length > 0 || (search.trim().length >= 2 && !loading && results.length === 0)) && (
        <div
          className="absolute left-0 right-0 mt-1 bg-[#0e0e16] border border-purple-500/40 rounded-lg shadow-xl max-h-64 overflow-y-auto z-20"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {loading ? (
            <div className="p-3 text-xs text-gray-400">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-xs text-gray-400">No crews match.</div>
          ) : (
            results.map((c) => (
              <button
                key={c.crew_id}
                type="button"
                onClick={() => { onChange(c); setSearch(c.name || ""); setResults([]); }}
                className="w-full text-left flex items-center gap-2 p-2 hover:bg-purple-500/10 border-b border-gray-800 last:border-b-0 transition-colors"
                data-testid={`${testIdPrefix}-result-${c.crew_id}`}
              >
                {c.cover_image
                  ? <img src={optimizeImage(c.cover_image, 'avatar')} alt={c.name} className="w-8 h-8 rounded-md object-cover flex-shrink-0" />
                  : <div className="w-8 h-8 rounded-md bg-purple-900/40 flex items-center justify-center text-xs flex-shrink-0">🎙️</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{c.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {c.member_count} members · {c.visibility || "—"}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CrewSeederRepairPanel = () => {
  const [open, setOpen] = useState(false);
  const [inviter, setInviter] = useState(null);
  const [joinedUser, setJoinedUser] = useState(null);
  const [crew, setCrew] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // History (auto-loads when inviter chosen)
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async (userId) => {
    setHistoryLoading(true);
    try {
      const r = await api.get(`/admin/crew-seeds/history/${userId}`);
      setHistory(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load seed history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (inviter?.user_id) fetchHistory(inviter.user_id);
    else setHistory(null);
  }, [inviter, fetchHistory]);

  const reset = () => {
    setInviter(null);
    setJoinedUser(null);
    setCrew(null);
    setReason("");
  };

  const memberCheckOK = !crew || !joinedUser
    || (crew.member_ids || []).includes(joinedUser.user_id);

  const handleSave = async () => {
    if (!inviter || !joinedUser || !crew) {
      toast.error("Pick an inviter, a joined member, and a crew");
      return;
    }
    if (!memberCheckOK) {
      toast.error("Joined user is not a member of the selected crew");
      return;
    }
    if (inviter.user_id === joinedUser.user_id) {
      toast.error("Inviter and joined member cannot be the same person");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/crew-seeds/assign", {
        crew_id: crew.crew_id,
        joined_user_id: joinedUser.user_id,
        inviter_user_id: inviter.user_id,
        reason: reason.trim() || null,
      });
      const parts = [
        `✓ Credited ${r.data.inviter_name || 'inviter'}`,
        r.data.duplicate_skipped ? '(duplicate skipped)' : null,
        r.data.crew_seeder_badge_awarded ? '🌱 Crew Seeder badge unlocked!' : null,
        `Total seeds: ${r.data.total_seeds}`,
      ].filter(Boolean);
      toast.success(parts.join(' · '));
      // Refresh history for the inviter
      await fetchHistory(inviter.user_id);
      // Reset joined-user + crew but keep inviter so admin can credit more seeds quickly
      setJoinedUser(null);
      setCrew(null);
      setReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not credit seed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="mb-6 p-4 bg-gradient-to-br from-emerald-900/20 to-emerald-800/5 border-emerald-500/30"
      data-testid="crew-seeder-repair-panel"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-white font-bold flex items-center gap-2">
          🌱 Crew Seeder repair
          {history?.total_seeds != null && (
            <span className="text-xs text-emerald-200/70 font-normal">
              · {history.total_seeds} seed(s) for {inviter?.artist_name}
            </span>
          )}
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { if (open) reset(); setOpen(!open); }}
          className="border-emerald-500/40 text-emerald-200"
          data-testid="toggle-crew-seeder-panel"
        >
          {open ? 'Close' : 'Open repair tool'}
        </Button>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <UserPicker
            label="Inviter (who should receive Crew Seeder credit)"
            selected={inviter}
            onChange={setInviter}
            testIdPrefix="seeder-inviter"
          />
          <UserPicker
            label="Joined member (the artist they brought in)"
            selected={joinedUser}
            onChange={setJoinedUser}
            exclude={inviter ? [inviter.user_id] : []}
            testIdPrefix="seeder-joined"
          />
          <CrewPicker
            label="Crew (where the join happened)"
            selected={crew}
            onChange={setCrew}
            requireMemberId={joinedUser?.user_id}
            testIdPrefix="seeder-crew"
          />
          <div>
            <label className="text-xs text-gray-400 block mb-1">Reason / repair notes</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Brought into crew before tracking shipped"
              className="bg-black/40 border-gray-700 text-white"
              data-testid="seeder-reason-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || !inviter || !joinedUser || !crew || !memberCheckOK}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-bold"
              data-testid="seeder-save"
            >
              {saving ? "Crediting…" : "Credit Crew Seed"}
            </Button>
            <Button
              variant="outline"
              onClick={reset}
              disabled={saving}
              className="border-gray-700 text-gray-300"
              data-testid="seeder-reset"
            >
              Reset
            </Button>
          </div>

          {/* History */}
          {inviter && (
            <div className="mt-3 pt-3 border-t border-emerald-500/20">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white text-sm font-semibold">
                  Seed history for {inviter.artist_name}
                </h3>
                {history?.has_crew_seeder_badge && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-200 border border-emerald-500/50">
                    🌱 Crew Seeder badge
                  </span>
                )}
              </div>
              {historyLoading ? (
                <div className="text-xs text-gray-400">Loading…</div>
              ) : !history?.seeds?.length ? (
                <div className="text-xs text-gray-500">No seeds yet for this inviter.</div>
              ) : (
                <div className="max-h-56 overflow-y-auto" data-testid="seeder-history-list">
                  {history.seeds.map((s) => (
                    <div
                      key={s.seed_id}
                      className="flex items-center gap-2 py-1.5 px-2 text-xs border-b border-gray-800/60 last:border-b-0"
                      data-testid={`seeder-history-row-${s.seed_id}`}
                    >
                      <span className="text-gray-500 shrink-0">{formatDate(s.created_at)}</span>
                      <span className="text-white truncate flex-1">
                        Brought <span className="text-emerald-300">{s.joined_artist_name || s.joined_user_id}</span>
                        {" "}into{" "}
                        <span className="text-purple-300">{s.crew_name || s.crew_id}</span>
                      </span>
                      <span className="text-[10px] text-gray-500">{s.kind}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default CrewSeederRepairPanel;
